import type { VersionLabel } from "#gql/catalogue"
import type { DefaultError, UndefinedInitialQueryOptions } from "@tanstack/vue-query"
import type { InferOutput } from "valibot"
import type { UnwrapRef } from "vue"
import { nullable, parse, union } from "valibot"

const catalogueSchema = union([
  productSchema,
  folderSchema,
  documentSchema,
])

type Catalogue = InferOutput<typeof catalogueSchema> | null

/**
 * Create query options for fetching a Crystallize catalogue.
 *
 * @param version - Either draft or published version of the catalogue.
 * @param path - The path to the Crystallize document, e.g. "/products" or "/products/shoes".
 */
export function createCrystallizeQueryOptions(
  version: MaybeRef<VersionLabel>,
  path: MaybeRefOrGetter<string>,
) {
  const queryKey = computed(() => [
    "crystallize",
    version,
    ...toValue(path).split("/").filter(Boolean),
  ] as const)

  return {
    queryKey,
    async queryFn({ queryKey }) {
      const [, version, ...parts] = queryKey

      const data = await GqlGetCatalogue({
        version,
        path: parts.join("/"),
      })

      return parse(
        nullable(catalogueSchema),
        data.catalogue,
      )
    },
  } satisfies UndefinedInitialQueryOptions<
    Catalogue,
    DefaultError,
    Catalogue,
    UnwrapRef<typeof queryKey>
  >
}
